import { Gauge, ServerOff, ShieldAlert } from '@orgus/ui/icons';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { machineLiveness } from '@orgus/protocol';
import type { Machine, MachineLoadEntry, MachineLoadState, WorkLeaseView, WorkWaiterView } from '@orgus/protocol';
import { BarList, DataState, MeterBar, RelativeTime, RowStatus, ShareBar, Skeleton, formatDuration, formatInt, formatPercent, plural } from '@orgus/ui';
import type { DataStateKind } from '@orgus/ui';
import { useConnection, useExt, useOrgus } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT, entryFor, fresh, groupName, groupTools, headroom, shareParts, shareSummary, slotOfKind } from './model.ts';

/** A heading inside the section: the title and one quiet fact. */
function Sub({ title, fact }: { title: string; fact?: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <h4 className="text-caption font-medium text-fg-muted">{title}</h4>
      {fact != null && <span className="min-w-0 truncate text-caption text-fg-subtle num">{fact}</span>}
    </div>
  );
}

function WorkRow({ status, what, meta, value, href }: { status: ReactNode; what: string; meta: string; value: string; href?: string }) {
  const body = (
    <>
      {status}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-mono text-caption text-fg">{what}</span>
        <span className="truncate text-caption text-fg-subtle">{meta}</span>
      </span>
      <span className="shrink-0 text-caption text-fg-muted num">{value}</span>
    </>
  );
  const cls = 'flex h-row-2line min-w-0 items-center gap-3 rounded-control px-2';
  return (
    <li className="motion-arrive">
      {href ? (
        <Link to={href} className={`${cls} transition-colors duration-(--duration-fast) hover:bg-hover`}>
          {body}
        </Link>
      ) : (
        <div className={cls}>{body}</div>
      )}
    </li>
  );
}

function useNames() {
  const sessions = useOrgus((s) => s.sessions);
  const projects = useOrgus((s) => s.projects);
  return useMemo(() => ({ sessions, projects }), [sessions, projects]);
}

/** The heavy commands holding slots and those waiting for them (ADR 0063). */
function HeavyWork({ work, now }: { work: NonNullable<MachineLoadEntry['work']>; now: number }) {
  const names = useNames();
  const { free, fit } = headroom(work);
  const where = (x: WorkLeaseView | WorkWaiterView) =>
    [x.projectId && names.projects[x.projectId]?.name, x.sessionId && names.sessions[x.sessionId]?.title].filter(Boolean).join(', ') || 'Started outside a session';
  const fact = `${formatInt(work.used)} of ${formatInt(work.budget)} slots${work.waiting.length ? `, ${formatInt(work.waiting.length)} waiting` : free ? `, room for ${plural(fit, 'more command')}` : ''}`;
  return (
    <section aria-label="Heavy work" className="flex min-w-0 flex-col gap-2">
      <Sub title="Heavy work" fact={fact} />
      <MeterBar value={work.budget ? work.used / work.budget : 0} tone={1} label={`Work budget: ${formatInt(work.used)} of ${formatInt(work.budget)} slots in use`} />
      {work.leases.length + work.waiting.length > 0 ? (
        <ul className="-mx-2 flex flex-col">
          {work.leases.map((l) => (
            <WorkRow
              key={l.id}
              status={<RowStatus tone="working" icon="working" label="Running" hideLabel focusable={false} />}
              what={l.what}
              meta={where(l)}
              value={`${plural(l.slots, 'slot')}, ${formatDuration(Math.max(0, now - l.since))}`}
              {...(l.sessionId ? { href: `/sessions?session=${encodeURIComponent(l.sessionId)}` } : {})}
            />
          ))}
          {work.waiting.map((w) => (
            <WorkRow
              key={w.id}
              status={<RowStatus tone="neutral" icon="queued" label="Waiting" hideLabel focusable={false} />}
              what={w.what}
              meta={where(w)}
              value={`Waits ${formatDuration(Math.max(0, now - w.since))}`}
              {...(w.sessionId ? { href: `/sessions?session=${encodeURIComponent(w.sessionId)}` } : {})}
            />
          ))}
        </ul>
      ) : (
        <p className="text-caption text-fg-subtle">No heavy commands running</p>
      )}
    </section>
  );
}

function AntivirusHint({ e, now }: { e: MachineLoadEntry; now: number }) {
  if (!e.antivirusHigh) return null;
  return (
    <div role="note" className="flex min-w-0 items-start gap-3 rounded-card bg-raised px-3 py-2">
      <span className="flex h-5 shrink-0 items-center">
        <ShieldAlert className="size-4 text-status-attention" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-ui text-fg">
          Antivirus has used {formatPercent(e.antivirusHigh.avgPct / 100)} of this machine for {formatDuration(Math.max(0, now - e.antivirusHigh.since))}
        </span>
        <span className="text-caption text-fg-subtle">
          It scans every file tests and builds write. Move your repositories to a Dev Drive or exclude your projects folder in Defender; both need an administrator.
        </span>
      </div>
    </div>
  );
}

function Breakdown({ e, now }: { e: MachineLoadEntry; now: number }) {
  const names = useNames();
  const items = e.groups.slice(0, 5).map((g, i) => ({
    key: `${g.kind}:${g.leaseId ?? g.sessionId ?? g.projectId ?? i}`,
    label: (
      <>
        {groupName(g, names)}
        {groupTools(g) && <span className="text-fg-subtle"> {groupTools(g)}</span>}
      </>
    ),
    value: g.pct / 100,
    other: g.kind === 'short-lived',
    slot: slotOfKind(g.kind),
  }));
  return (
    <div className="flex min-w-0 flex-col gap-6">
      {items.length > 0 && (
        <section aria-label="Top drivers" className="flex min-w-0 flex-col gap-3">
          <Sub title="Top drivers" fact={e.windowMs ? `over the last ${formatDuration(e.windowMs)}` : undefined} />
          <BarList items={items} format={(n) => formatPercent(n)} slotOf={(_item, i) => items[i]?.slot ?? 1} label="What uses this machine's CPU, busiest first" />
        </section>
      )}
      <AntivirusHint e={e} now={now} />
      {e.work && <HeavyWork work={e.work} now={now} />}
    </div>
  );
}

function SectionSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-2 w-full" />
      <Skeleton className="h-3 w-2/3" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

export function loadStateOf(o: { phase: string; slice: MachineLoadState | undefined; entry: MachineLoadEntry | undefined; online: boolean; now: number }): DataStateKind {
  if (o.phase === 'loading' && !o.entry) return 'loading';
  if (!o.online) return 'empty';
  if (!o.entry) return 'error';
  if (!o.entry.work || !o.entry.windowMs || !fresh(o.entry, o.now)) return 'partial';
  if (o.entry.load < 0.01 && !o.entry.groups.length && !o.entry.work.leases.length && !o.entry.work.waiting.length) return 'empty';
  return 'full';
}

/**
 * "What's using this machine" (ADR 0063 step 4): the CPU split between heavy work, sessions,
 * projects and background, the busiest groups, the work budget with its leases and waiters, and a
 * hint when antivirus keeps a large share. Live from the hub module 'machine-load'.
 */
export function MachineLoad({ machine: m, now }: { machine: Machine; now: number }) {
  const slice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const { phase } = useConnection();
  const e = entryFor(slice, m.id);
  const online = machineLiveness(m, now) === 'online';
  const state = loadStateOf({ phase, slice, entry: e, online, now });
  const partial = !e
    ? null
    : !fresh(e, now)
      ? (
          <>
            The last breakdown is <RelativeTime ts={e.at} />, so this may be out of date.
          </>
        )
      : !e.windowMs
        ? 'Measuring: the first breakdown arrives within 30 seconds.'
        : !e.work
          ? 'Could not read the work budget folder on this machine, so heavy commands are missing.'
          : null;
  return (
    <section aria-label="What's using this machine" className="flex min-w-0 flex-col gap-4">
      <div className="flex h-control-sm min-w-0 items-center gap-2 text-caption">
        <h3 className="font-medium text-fg-muted">What's using this machine</h3>
        {e && online && (
          <span className="ml-auto shrink-0 text-caption text-fg-subtle">
            <RelativeTime ts={e.at} />
          </span>
        )}
      </div>
      <DataState
        state={state}
        skeleton={<SectionSkeleton />}
        loadingLabel="Loading what uses this machine"
        empty={
          online
            ? { icon: <Gauge />, title: 'Nothing is using this machine', description: 'No heavy commands and no busy processes right now.' }
            : { icon: <ServerOff />, title: 'No reading while offline', description: 'What uses this machine shows again when its agent reconnects.' }
        }
        error={{
          what: 'No load breakdown from this machine.',
          why: slice ? 'Its agent does not send one: it predates the machine load module, or it cannot read the process table.' : 'This hub does not run the machine load module.',
          next: slice ? 'Update the agent on this machine; the breakdown starts within 30 seconds.' : 'Update Orgus on the hub.',
        }}
        partial={partial}
      >
        {e && (
          <div className="flex min-w-0 flex-col gap-6">
            {e.windowMs > 0 && <ShareBar parts={shareParts(e)} label={shareSummary(m.name, e)} />}
            <Breakdown e={e} now={now} />
          </div>
        )}
      </DataState>
    </section>
  );
}
